import { FC, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Table, Spin, Row, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Content, Loader, PageSectionHeader } from '@/components';
import { NAMESPACE } from '@/constants';
import type { CalculatorPackage } from '@/api/calculatorPackages';
import { useCustomPackages } from './hooks/useCustomPackages';
import { ActionBar, StyledContainer } from './CustomPackages.styles';
import { CalculatorPackageModal } from './components/CalculatorPackageModal';
import { buildDuplicateInitialPackage } from './buildDuplicateInitialPackage';
import { PRIVATE_PATH } from '@/constants/paths';

const { Search } = Input;

const renderTotal = (total: number) => `Total ${total} items`;

type PackageModalState = null | { mode: 'create' } | { mode: 'duplicate'; source: CalculatorPackage };

const CustomPackages: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const navigate = useNavigate();
  const [packageModal, setPackageModal] = useState<PackageModalState>(null);
  const {
    list,
    columns,
    count,
    page,
    limit,
    isFetching,
    isLoading,
    handlePaginationChange,
    channelFilter,
    setChannelFilter,
    channelOptions,
    handleSearch
  } = useCustomPackages({
    onDuplicatePackage: (pkg) => setPackageModal({ mode: 'duplicate', source: pkg })
  });

  const handleCloseModal = () => {
    setPackageModal(null);
  };

  const handlePackageSaved = (savedPackage?: CalculatorPackage) => {
    handleCloseModal();
    if (savedPackage?._id) {
      navigate(`${PRIVATE_PATH.CUSTOM_PACKAGE}/${savedPackage._id}`);
    }
  };

  const duplicateInitialPackage = useMemo(() => {
    if (packageModal?.mode !== 'duplicate') return null;
    return buildDuplicateInitialPackage(packageModal.source);
  }, [packageModal?.mode === 'duplicate' ? packageModal.source._id : null]);

  return (
    <>
      <PageSectionHeader>{t('customPackagesTitle')}</PageSectionHeader>
      <Content>
        <StyledContainer>
          <ActionBar justify="space-between" align="middle">
            <Space>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={() => setPackageModal({ mode: 'create' })}
              >
                {t('buildPackage')}
              </Button>
              <Search
                size="large"
                allowClear
                style={{ minWidth: 280 }}
                placeholder={t('searchPackagesPlaceholder')}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
              />
              <Select
                size="large"
                placeholder={t('channel')}
                allowClear
                style={{ minWidth: 160 }}
                value={channelFilter ?? ''}
                onChange={(v) => setChannelFilter(v || undefined)}
                options={[{ value: '', label: 'All channels' }, ...channelOptions]}
              />
            </Space>
          </ActionBar>
          {isLoading ? (
            <Loader fullscreen={false} />
          ) : (
            <Table<import('@/api/calculatorPackages').CalculatorPackage>
              columns={columns}
              dataSource={list.map((p) => ({ ...p, key: p._id }))}
              loading={isFetching}
              scroll={{ x: 720 }}
              pagination={{
                total: count,
                current: page,
                pageSize: limit,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: renderTotal,
                onChange: handlePaginationChange
              }}
            />
          )}
        </StyledContainer>
      </Content>

      {packageModal !== null && (
        <CalculatorPackageModal
          key={packageModal.mode === 'duplicate' ? `dup-${packageModal.source._id}` : 'create'}
          open
          onClose={handleCloseModal}
          onSuccess={handlePackageSaved}
          packageId={null}
          isDuplicating={packageModal.mode === 'duplicate'}
          duplicateSourcePackageId={
            packageModal.mode === 'duplicate' ? packageModal.source._id : undefined
          }
          initialPackage={packageModal.mode === 'duplicate' ? duplicateInitialPackage : null}
        />
      )}
    </>
  );
};

export default CustomPackages;
